import {
  Box,
  Flex,
  useColorModeValue,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import StatsCards from "components/card/StatsCards";
import DashboardAnalyticsCharts from "./DashboardAnalyticsCharts";
import Filters from "./components/Filters";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import ReadyOrdersReport from "./ReadyOrdersReport";
import PerformanceReport from "./PerformanceReport";
import SalesReport from "./SalesReport";

export default function UserReports() {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [dateFilter, setDateFilter] = useState("today");
  const [branch, setBranch] = useState("all");
  const [companyId, setCompanyId] = useState("");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const toast = useToast();
  const brandColor = useColorModeValue("cyan.500", "white");

  // Fetch analytics
  useEffect(() => {
    if (dateFilter === "custom" && (!customRange.from || !customRange.to)) {
      setAnalytics(null);
      return;
    }

    const data = {
      type: "admin",
      date_filter: dateFilter,
    };
      if (companyId && companyId !== "all") data.company_id = companyId;
      if (branch && branch !== "all") data.branch_id = branch;
    if (dateFilter === "custom") {
      data.from = customRange.from;
      data.to = customRange.to;
    }

    fetchWithToken(`${process.env.REACT_APP_API_URL_Report}analytics/orders`, "POST", data)
      .then((res) => setAnalytics(res))
      .catch((err) => {
        setAnalytics(null);
        console.error("Analytics error:", err);
      });
  }, [dateFilter, branch, customRange, companyId]);

  // Prepare stats
  const miniStatsData =
    dateFilter === "custom" && (!customRange.from || !customRange.to)
      ? {
          OrdersToday: 0,
          DeliveriesToday: 0,
          OrderCount: 0,
          OrderAmount: 0,
        }
      : analytics?.data
      ? {
          OrdersToday: analytics.data.orders_today,
          DeliveriesToday: analytics.data.deliveries_today,
          OrderCount: analytics.data.order_count,
          OrderAmount: analytics.data.order_amount,
        }
      : {
          OrdersToday: 0,
          DeliveriesToday: 0,
          OrderCount: 0,
          OrderAmount: 0,
        };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Filters */}
<Flex mb={4} justify="flex-end" mr={6}>
        <Filters
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          branch={branch}
          setBranch={setBranch}
          companyId={companyId}
          setCompanyId={setCompanyId}
          customRange={customRange}
          setCustomRange={setCustomRange}
          showDateFilter={true}
          showBranchFilter={true}
          showStatusFilter={false}
          showOrdersPerPage={false}
          showCompanyFilter={true}
          showServiceFilter={false}
        />
      </Flex>

      {/* Stats Cards */}
      <StatsCards stats={miniStatsData} />

      {/* Charts */}
      <DashboardAnalyticsCharts
        type="admin"
        companyId={companyId}
        branchId={branch}
        dateFilter={dateFilter}
        customRange={customRange}
      />

      {/* Tabs for Reports */}
      <Box mt={8}>
        <Tabs
          variant="enclosed"
          colorScheme="blue"
          isFitted
          sx={{
            '.chakra-tabs__tab': {
              transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
              fontWeight: 'bold',
              fontSize: 'lg',
              color: 'gray.500',
              _selected: {
                color: 'blue.500',
                bg: 'white',
                boxShadow: '0 4px 20px 0 rgba(66,153,225,0.10)',
                transform: 'scale(1.08)',
                zIndex: 1,
              },
              _hover: {
                color: 'blue.400',
                bg: 'gray.50',
                transform: 'scale(1.04)',
              },
            },
            '.chakra-tabs__tab-panel': {
              animation: 'fadeInTab 0.5s',
              bg: 'white',
              borderRadius: 'md',
              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
              p: 4,
            },
            '@keyframes fadeInTab': {
              from: { opacity: 0, transform: 'translateY(20px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <TabList mb="1em" borderBottom="2px solid" borderColor={useColorModeValue("gray.200", "gray.700")}> 
            <Tab>Ready Orders Report</Tab>
            <Tab>Performance Report</Tab>
            <Tab>Sales Report</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <ReadyOrdersReport />
            </TabPanel>
            <TabPanel>
              <PerformanceReport />
            </TabPanel>
            <TabPanel>
              <SalesReport />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
